import React, { useState, useEffect } from 'react'
import { api } from '../../lib/api'
import { Loader2, Lock, ChevronDown, ChevronUp, Megaphone } from 'lucide-react'

/**
 * The private groups the viewer belongs to within a parish, shown on the parish
 * page beneath the parish announcements. Expanding a group reveals its
 * announcements.
 *
 * Groups are hidden from non-members: the list endpoint only ever returns groups
 * the caller is a member of (the owning parish admin additionally sees all of
 * their parish's groups). When it returns nothing this renders nothing at all —
 * no heading, no empty state — so there is never a hint that a group exists.
 */
export default function MemberGroupsSection({ parishId }) {
  const [groups, setGroups] = useState([])
  const [loading, setLoading] = useState(true)
  const [expandedGroupId, setExpandedGroupId] = useState(null)
  const [announcementsByGroup, setAnnouncementsByGroup] = useState({})
  const [loadingGroupId, setLoadingGroupId] = useState(null)
  const [errorByGroup, setErrorByGroup] = useState({})

  // Members are only served published announcements; the owning admin also gets
  // drafts, which don't belong in this member-facing view.
  const liveOnly = (data) => {
    const now = new Date()
    return (data || []).filter(
      (a) => a.isPublished !== false && (!a.publishedAt || new Date(a.publishedAt) <= now),
    )
  }

  useEffect(() => {
    let cancelled = false

    const fetchGroupsAndAnnouncements = async () => {
      if (!parishId) return
      try {
        setLoading(true)
        const data = await api.getParishGroups(parishId)
        const visibleGroups = data || []
        if (cancelled) return
        setGroups(visibleGroups)

        // Announcements are loaded up front, not on expand, so each group can
        // show its announcement count while collapsed.
        const results = await Promise.all(
          visibleGroups.map(async (group) => {
            try {
              return { groupId: group.id, announcements: liveOnly(await api.getGroupAnnouncements(group.id)) }
            } catch (err) {
              console.error(`Failed to fetch announcements for group ${group.id}:`, err)
              return { groupId: group.id, error: err.message || 'Failed to load announcements' }
            }
          }),
        )
        if (cancelled) return

        const byGroup = {}
        const errors = {}
        results.forEach(({ groupId, announcements, error }) => {
          if (error) errors[groupId] = error
          else byGroup[groupId] = announcements
        })
        setAnnouncementsByGroup(byGroup)
        setErrorByGroup(errors)
      } catch (err) {
        // Log only — a visible error state would itself signal that something
        // is here to fail.
        console.error('Failed to fetch groups:', err)
        if (!cancelled) setGroups([])
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    fetchGroupsAndAnnouncements()
    return () => {
      cancelled = true
    }
  }, [parishId])

  const fetchGroupAnnouncements = async (groupId) => {
    try {
      setLoadingGroupId(groupId)
      setErrorByGroup((prev) => ({ ...prev, [groupId]: null }))
      const data = await api.getGroupAnnouncements(groupId)
      setAnnouncementsByGroup((prev) => ({ ...prev, [groupId]: liveOnly(data) }))
    } catch (err) {
      console.error('Failed to fetch group announcements:', err)
      setErrorByGroup((prev) => ({
        ...prev,
        [groupId]: err.message || 'Failed to load announcements',
      }))
    } finally {
      setLoadingGroupId(null)
    }
  }

  const handleToggle = (groupId) => {
    if (expandedGroupId === groupId) {
      setExpandedGroupId(null)
      return
    }
    setExpandedGroupId(groupId)
    // Normally already loaded with the group list — this only retries a group
    // whose announcements failed to load.
    if (!announcementsByGroup[groupId]) {
      fetchGroupAnnouncements(groupId)
    }
  }

  if (loading || groups.length === 0) return null

  return (
    <div className="bg-white border border-surface-border rounded p-6">
      <div className="flex items-center gap-2 mb-4">
        <Lock className="w-5 h-5 text-gold" />
        <h3 className="text-xl font-semibold font-display text-charcoal">My Groups</h3>
      </div>

      <div className="space-y-3">
        {groups.map((group) => {
          const isExpanded = expandedGroupId === group.id
          const announcements = announcementsByGroup[group.id] || []
          const groupError = errorByGroup[group.id]
          const hasCount = Boolean(announcementsByGroup[group.id])
          const countLabel =
            announcements.length === 1 ? '1 announcement' : `${announcements.length} announcements`

          return (
            <div key={group.id} className="border border-surface-border rounded overflow-hidden">
              <button
                type="button"
                onClick={() => handleToggle(group.id)}
                aria-expanded={isExpanded}
                className="w-full flex items-start justify-between gap-3 p-4 hover:bg-surface-linen transition-colors text-left"
              >
                <div className="min-w-0">
                  <span className="font-semibold text-charcoal">{group.name}</span>
                  {group.description && (
                    <p className="text-sm text-charcoal-muted mt-1">{group.description}</p>
                  )}
                </div>
                <div className="flex flex-col items-end gap-2 flex-shrink-0">
                  {isExpanded ? (
                    <ChevronUp className="w-4 h-4 text-charcoal-subtle" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-charcoal-subtle" />
                  )}
                  {hasCount && (
                    <span className="inline-flex items-center gap-1 bg-gray-100 rounded-sm px-2 py-0.5 text-xs font-semibold text-charcoal whitespace-nowrap">
                      <Megaphone className="w-3 h-3 text-gold" />
                      {countLabel}
                    </span>
                  )}
                </div>
              </button>

              {isExpanded && (
                <div className="border-t border-surface-border p-4">
                  {loadingGroupId === group.id ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="w-6 h-6 text-gold animate-spin" />
                    </div>
                  ) : groupError ? (
                    <p className="text-charcoal-muted text-sm">{groupError}</p>
                  ) : announcements.length === 0 ? (
                    <p className="text-charcoal-muted text-sm">
                      No announcements in this group yet.
                    </p>
                  ) : (
                    <>
                      <div className="flex items-center gap-2 bg-gray-100 rounded px-3 py-2 mb-3">
                        <Megaphone className="w-4 h-4 text-gold" />
                        <h4 className="text-sm font-semibold text-charcoal">Latest Announcements</h4>
                      </div>
                      <div className="space-y-4">
                        {announcements.map((announcement, index) => (
                          <div
                            key={announcement.id}
                            className={`${index < announcements.length - 1 ? 'pb-4 border-b border-surface-border' : ''}`}
                          >
                            <h4 className="text-base font-semibold text-charcoal">
                              {announcement.title}
                            </h4>
                            <p className="text-sm text-charcoal/90 leading-relaxed mt-1 whitespace-pre-wrap">
                              {announcement.content}
                            </p>
                            <div className="flex items-center gap-3 mt-2 text-xs text-charcoal-muted">
                              {announcement.publishedAt && (
                                <span>
                                  {new Date(announcement.publishedAt).toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric',
                                  })}
                                </span>
                              )}
                              {announcement.author && (
                                <span>by {announcement.author.name || 'Parish Staff'}</span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
